import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, X, Heart } from "lucide-react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import SectionHeader from "@/components/SectionHeader";
import { DOMAINS, TOTAL_PORTFOLIO_VALUE, WHY_DOMAINS } from "@/data/site";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

const formatUSD = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

function IconBlock({ name, className = "" }) {
  const Cmp = Icons[name] || Icons.Circle;
  return <Cmp className={className} strokeWidth={1.5} />;
}

function OfferDialog({ open, domain, onClose }) {
  const [form, setForm] = useState({ name: "", company: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", {
        ...form,
        project_type: "Domain Portfolio Inquiry",
        submission_kind: "offer",
        domain,
        message: `[Domain inquiry: ${domain}]\n\n${form.message}`,
      });
      toast.success("Offer submitted. Our team will respond shortly.");
      onClose();
    } catch (err) {
      toast.error("Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      data-testid="offer-dialog"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg tracing-card p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-500 hover:text-slate-900"
          data-testid="offer-dialog-close"
        >
          <X size={18} />
        </button>
        <div className="eyebrow mb-3">// Submit Inquiry</div>
        <h3 className="font-display text-2xl font-semibold text-slate-900">Make an Offer</h3>
        <p className="mt-2 text-sm text-slate-600">
          Domain: <span className="text-[#0066FF] font-mono">{domain}</span>
        </p>
        <p className="mt-1 text-xs text-slate-500">
          Note: S&B Technologies is accepting offers on the entire portfolio only.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="offer-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              required
              type="text"
              placeholder="Your name *"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              className="w-full bg-white border border-slate-300 px-4 py-3 text-sm text-slate-900 focus-ring outline-none transition-colors"
              data-testid="offer-input-name"
            />
            <input
              type="text"
              placeholder="Company"
              value={form.company}
              onChange={(e) => set("company", e.target.value)}
              className="w-full bg-white border border-slate-300 px-4 py-3 text-sm text-slate-900 focus-ring outline-none transition-colors"
              data-testid="offer-input-company"
            />
          </div>
          <input
            required
            type="email"
            placeholder="Email *"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            className="w-full bg-white border border-slate-300 px-4 py-3 text-sm text-slate-900 focus-ring outline-none transition-colors"
            data-testid="offer-input-email"
          />
          <textarea
            required
            rows={4}
            placeholder="Your offer or message *"
            value={form.message}
            onChange={(e) => set("message", e.target.value)}
            className="w-full bg-white border border-slate-300 px-4 py-3 text-sm text-slate-900 focus-ring outline-none transition-colors resize-none"
            data-testid="offer-input-message"
          />
          <button
            type="submit"
            disabled={submitting}
            className="btn-gold w-full justify-center disabled:opacity-50"
            data-testid="offer-submit-btn"
          >
            {submitting ? "Submitting..." : "Submit Offer"} <ArrowRight size={14} />
          </button>
        </form>
      </div>
    </div>
  );
}

export default function DomainPortfolio() {
  const [offerOpen, setOfferOpen] = useState(false);
  const [activeDomain, setActiveDomain] = useState("");
  const { user, loginWithGoogle } = useAuth();
  const [favorites, setFavorites] = useState(new Set());

  useEffect(() => {
    if (!user) {
      setFavorites(new Set());
      return;
    }
    api
      .get("/me/favorites", { withCredentials: true })
      .then((res) => setFavorites(new Set(res.data || [])))
      .catch(() => {});
  }, [user]);

  const toggleFav = async (domain) => {
    if (!user) {
      toast.info("Sign in with Google to save favorites.");
      loginWithGoogle();
      return;
    }
    const isFav = favorites.has(domain);
    // optimistic
    const next = new Set(favorites);
    if (isFav) next.delete(domain);
    else next.add(domain);
    setFavorites(next);
    try {
      if (isFav) {
        await api.delete(`/me/favorites/${encodeURIComponent(domain)}`, { withCredentials: true });
        toast.success("Removed from favorites");
      } else {
        await api.post("/me/favorites", { domain }, { withCredentials: true });
        toast.success("Saved to favorites");
      }
    } catch {
      // rollback
      setFavorites(favorites);
      toast.error("Failed to update favorites");
    }
  };

  const openOffer = (d) => {
    setActiveDomain(d);
    setOfferOpen(true);
  };

  return (
    <>
      <section className="pt-32 pb-12 md:pt-40 md:pb-16 relative" data-testid="domain-portfolio-page">
        <div className="absolute inset-0 grid-bg radial-mask opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              <div className="eyebrow mb-4">Digital Real Estate</div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900">
                Premium Digital Real Estate Portfolio.
              </h1>
              <p className="mt-6 text-base md:text-lg text-slate-600 max-w-2xl leading-relaxed">
                S&amp;B Technologies owns, consults on, and sells premium domain assets designed
                to create online authority, lead generation power, and long-term digital value.
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="tracing-card p-8">
                <div className="eyebrow mb-2">// Certified Total Value</div>
                <div className="font-display text-5xl font-semibold text-[#C8961F]">
                  {formatUSD(TOTAL_PORTFOLIO_VALUE)}
                </div>
                <div className="mt-2 text-sm text-slate-500">USD · Arizona Real Estate Portfolio</div>
                <div className="mt-6 pt-6 border-t border-slate-200 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Domains</span>
                    <span className="text-slate-900 font-medium">{DOMAINS.length}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Acquisition Mode</span>
                    <span className="text-[#0066FF] font-medium">Portfolio Only</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Partnership Offer</span>
                    <span className="text-[#C8961F] font-medium">$250,000</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="tracing-card overflow-hidden">
            <div className="grid grid-cols-12 gap-4 px-6 md:px-8 py-4 border-b border-[#0066FF]/20 bg-white/60">
              <div className="col-span-1 text-[0.65rem] font-mono uppercase tracking-[0.2em] text-slate-500">
                #
              </div>
              <div className="col-span-6 md:col-span-7 text-[0.65rem] font-mono uppercase tracking-[0.2em] text-slate-500">
                Domain
              </div>
              <div className="col-span-3 md:col-span-2 text-right text-[0.65rem] font-mono uppercase tracking-[0.2em] text-slate-500">
                Cert. Value
              </div>
              <div className="col-span-2 text-right text-[0.65rem] font-mono uppercase tracking-[0.2em] text-slate-500">
                Action
              </div>
            </div>
            {DOMAINS.map((d, i) => {
              const isFav = favorites.has(d.name);
              return (
              <div
                key={d.name}
                data-testid={`domain-row-${i}`}
                className="grid grid-cols-12 gap-4 px-6 md:px-8 py-5 items-center border-b border-slate-100 hover:bg-slate-50 transition-colors"
              >
                <div className="col-span-1 font-mono text-xs text-slate-400">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="col-span-5 md:col-span-7">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => toggleFav(d.name)}
                      className={`p-1 transition-colors ${isFav ? "text-red-500" : "text-slate-300 hover:text-red-400"}`}
                      data-testid={`domain-fav-btn-${i}`}
                      title={user ? (isFav ? "Remove from favorites" : "Save to favorites") : "Sign in to save"}
                    >
                      <Heart size={16} fill={isFav ? "currentColor" : "none"} strokeWidth={1.5} />
                    </button>
                    <div className="font-mono text-sm md:text-base text-slate-900">{d.name}</div>
                  </div>
                  <div className="text-xs text-slate-500 mt-1 ml-7">Certified Estimated Value</div>
                </div>
                <div className="col-span-4 md:col-span-2 text-right font-display text-base md:text-lg font-semibold text-[#C8961F]">
                  {formatUSD(d.value)}
                </div>
                <div className="col-span-2 text-right">
                  <button
                    onClick={() => openOffer(d.name)}
                    data-testid={`domain-offer-btn-${i}`}
                    className="text-xs font-mono uppercase tracking-[0.15em] text-[#0066FF] hover:text-slate-900 border border-[#0066FF]/40 hover:border-[#0066FF] px-3 py-2 transition-colors"
                  >
                    Make Offer
                  </button>
                </div>
              </div>
              );
            })}
            <div className="grid grid-cols-12 gap-4 px-6 md:px-8 py-6 bg-[#C8961F]/[0.04]">
              <div className="col-span-7 md:col-span-8 font-display text-lg font-semibold text-slate-900">
                Total Certified Portfolio Value
              </div>
              <div className="col-span-5 md:col-span-4 text-right font-display text-2xl md:text-3xl font-semibold text-[#C8961F]">
                {formatUSD(TOTAL_PORTFOLIO_VALUE)}
              </div>
            </div>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="tracing-card p-8">
              <div className="eyebrow mb-3">// Acquisition Terms</div>
              <h3 className="font-display text-xl font-semibold text-slate-900">Portfolio Only</h3>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                S&amp;B Technologies is accepting offers on the entire portfolio only.
                Individual domains are not currently being offered separately. Serious
                qualified inquiries only.
              </p>
            </div>
            <div className="tracing-card p-8 border-[#C8961F]/30">
              <div className="eyebrow mb-3" style={{ color: "#C8961F" }}>
                // Strategic Partnership
              </div>
              <h3 className="font-display text-xl font-semibold text-slate-900">$250,000</h3>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                Strategic partnership investment opportunity available at $250,000 with{" "}
                <span className="text-slate-900">50% down</span> and the remaining balance
                payable over <span className="text-slate-900">12 months</span>.
              </p>
              <Link
                to="/contact?type=Investor%20%2F%20Partnership%20Opportunity"
                className="mt-5 btn-gold inline-flex"
                data-testid="partnership-cta-btn"
              >
                Request Terms <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Domains */}
      <section id="why" className="py-24 md:py-32 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeader
            eyebrow="Thesis"
            title="Why premium domains matter."
            description="Premium domains are digital real estate. Scarce, memorable, searchable, brandable, and capable of generating long-term value."
          />
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHY_DOMAINS.map((w, i) => (
              <div key={w.title} className="tracing-card p-6 group" data-testid={`dp-why-${i}`}>
                <div className="flex h-11 w-11 items-center justify-center border border-[#0066FF]/30 text-[#0066FF] mb-5 group-hover:border-[#0066FF]">
                  <IconBlock name={w.icon} className="h-5 w-5" />
                </div>
                <h3 className="font-display text-base font-semibold text-slate-900">{w.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <OfferDialog
        open={offerOpen}
        domain={activeDomain}
        onClose={() => setOfferOpen(false)}
      />
    </>
  );
}
